// src/CastingProjectPage.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "./navigation";
import {
  Snackbar,
  Alert,
  IconButton,
  Box,
} from "@mui/material";

import ReplyDrawer from "./ReplyDrawer";
import SubmissionTable from "./director/components/Submission/SubmissionTable";
import SharePopup from "./director/components/Submission/SharePopup";
import NoticePreviewDialog from "./director/components/NoticePreviewDialog";
import { fetchTalentNotes } from "./director/components/Submission/TalentNoteService";

import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import AppLoader from "./components/common/AppLoader";

const CastingProjectPage = () => {
  const userId = localStorage.getItem("userId");
  const email = localStorage.getItem("email");
  const { noticeId } = useParams();
  const navigate = useNavigate();

  // Page state
  const [projectTitle, setProjectTitle] = useState("Loading...");
  const [submissions, setSubmissions] = useState([]);
  const [expandedList, setExpandedList] = useState({ roleId: null, listName: null });
  const [talentDetails, setTalentDetails] = useState([]);
const [sortBy, setSortBy] = useState("MostRecent");
  const [favorites, setFavorites] = useState({});
  const [notes, setNotes] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [moveStatus, setMoveStatus] = useState(false);

  // Messaging
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isReplyDrawerOpen, setIsReplyDrawerOpen] = useState(false);

  // Share
  const shareMenuRef = useRef(null);
  const [sharePos, setSharePos] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [shareNotice, setShareNotice] = useState(null);

  // View Notice
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [noticeDialogOpen, setNoticeDialogOpen] = useState(false);

  // Selections
  const [selectedTalents, setSelectedTalents] = useState([]);

  // Loaders
  const [isPageLoading, setIsPageLoading] = useState(true);   // full page loader (title + submissions)
  const [isRowLoading, setIsRowLoading] = useState(false);    // expanding a list row

  // Errors
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const showError = (msg) => setSnackbar({ open: true, message: msg, severity: "error" });
  const showInfo = (msg) => setSnackbar({ open: true, message: msg, severity: "success" });

  // Helpers
  const applySorting = (data, key) => {
    if (!data?.length) return [];
    return [...data].sort((a, b) => {
      if (key === "MostRecent") return new Date(b.recordCreatedDate) - new Date(a.recordCreatedDate);
      if (key === "MostLikes") return (b.likes || 0) - (a.likes || 0);
      return 0;
    });
  };
const cleanIds = (str) => {
  if (!str) return [];

  return str
    .split(",")
    .map((id) => id.trim())
    .filter(
      (id) =>
        id &&
        id !== "null" &&
        id !== "undefined" &&
        id !== "0"
    );
};

const parseRolesFromCasting = (castingData, noticeId) => {
  const notice = (castingData?.data || []).find(
    (item) => String(item.noticeId) === String(noticeId)
  );

  return notice?.roles?.map((role) => ({
    roleId: role.roleId,
    roleTitle: role.rolename,
    aListUserIds: [],
    bListUserIds: [],
    cListUserIds: [],
    xListUserIds: [],
    newUserIds: [],
  })) || [];
};

const parseSubmissions = (data) =>
  (data?.data || []).map((role) => ({
    roleId: role.roleId,
    roleTitle: role.roleName,
    aListUserIds: cleanIds(role.aListUserIds),
    bListUserIds: cleanIds(role.bListUserIds),
    cListUserIds: cleanIds(role.cListUserIds),
    xListUserIds: cleanIds(role.xListUserIds),
    newUserIds: cleanIds(role.newUserIds),
  }));

const mergeSubmissions = (roles, submissions) => {
  const map = new Map();

  submissions.forEach((s) => {
    map.set(s.roleId, s);
  });

  return roles.map((role) => {
    const sub = map.get(role.roleId);

    if (!sub) return role; // no submissions → keep empty arrays

    return {
      ...role,
      aListUserIds: sub.aListUserIds || [],
      bListUserIds: sub.bListUserIds || [],
      cListUserIds: sub.cListUserIds || [],
      xListUserIds: sub.xListUserIds || [],
      newUserIds: sub.newUserIds || [],
    };
  });
};

  // Initial load: Project Title + Submissions
useEffect(() => {
  let cancelled = false;

  const load = async () => {
    setIsPageLoading(true);

    try {
      // 1. Get casting details (ALL roles)
      const castingRes = await api.get(
        ENDPOINTS.getNoticeDetails({ email, noticeId, page: 1, pageSize: 50 })
      );

      const titleRecord = castingRes?.data?.data?.find(
        (item) => String(item.noticeId) === String(noticeId)
      );

      if (!cancelled) {
        setProjectTitle(titleRecord?.projectTitle || "Unknown Project");
      }

      const roles = parseRolesFromCasting(castingRes?.data, noticeId);

      // 2. Get submissions
      const subsRes = await api.get(
        ENDPOINTS.getProjectSubmissions({ noticeId })
      );

      const parsedSubs = parseSubmissions(subsRes?.data);

      // 3. Merge roles + submissions
      const finalData = mergeSubmissions(roles, parsedSubs);

      if (!cancelled) {
        setSubmissions(finalData);
        setTalentDetails([]);
        setExpandedList({ roleId: null, listName: null });
      }

    } catch (err) {
      if (!cancelled) {
        setProjectTitle("Error Loading Title");
        setSubmissions([]);
        showError("Failed to load project details.");
      }
    } finally {
      if (!cancelled) setIsPageLoading(false);
    }
  };

  load();
  return () => { cancelled = true; };
}, [noticeId, email]);


  // Expand list -> fetch talent
const fetchTalentDetails = async (title, userIds, roleId, listName) => {
  if (!userIds) return;

  const cleanedUserIds = userIds.replace(/\s+/g, "");
  const isSameRow =
    expandedList.roleId === roleId &&
    expandedList.listName === listName;

  if (isSameRow) {
    setExpandedList({ roleId: null, listName: null });
    setTalentDetails([]);
    return;
  }

  setExpandedList({ roleId, listName });
  setIsRowLoading(true);

  try {
    const res = await api.get(
      ENDPOINTS.getTalentDetails({
        userIds: cleanedUserIds,
        roleId,
        clientId: userId,
        sortBy:
          sortBy === "Newest"
            ? "Newest"
            : sortBy === "MostLikes"
            ? "MostLikes"
            : sortBy === "Experience"
            ? "Experience"
            : "Newest",
      })
    );

const rows = applySorting(res?.data?.data || [], sortBy);

const rowsWithNotes = await Promise.all(
  rows.map(async (row) => {
    const note = await fetchTalentNotes({
      talentId: row.userId,
      directorId: userId,
    });

    return {
      ...row,
      note: note?.text || "",
      noteId: note?.noteId || 0,
    };
  })
);

setTalentDetails(rowsWithNotes);
  } catch (err) {
    setTalentDetails([]);
    showError("Failed to load talent details.");
  } finally {
    setIsRowLoading(false);
  }
};
  // Fave toggle
  const handleFavouriteClick = async (e, talentId, currentFave) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const newFave = !currentFave;
    const formData = new FormData();
    formData.append("clientId", userId);
    formData.append("talentId", talentId);
    formData.append("Fave", newFave ? "true" : "false");

    setFavorites((prev) => ({ ...prev, [talentId]: newFave }));
    try {
      await api.post(ENDPOINTS.addOrUpdateFave(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (err) {
      // revert on error
      setFavorites((prev) => ({ ...prev, [talentId]: currentFave }));
      showError("Failed to update favourite.");
    }
  };

  // List codes
  const listCodeMap = {
    "A List": "A",
    "B List": "B",
    "C List": "C",
    "X List": "X",
    New: "N",
    A: "A",
    B: "B",
    C: "C",
    X: "X",
    N: "N",
  };

  const listKeyMap = {
    A: "aListUserIds",
    B: "bListUserIds",
    C: "cListUserIds",
    X: "xListUserIds",
    N: "newUserIds",
  };

  // Move talent between lists
  const handleMoveTalent = async (uid, roleId, currentList, targetList) => {
    if (currentList === targetList) return;

    const payload = {
      userId: String(uid),
      roleId: Number(roleId),
      currentList: listCodeMap[currentList],
      targetList: listCodeMap[targetList],
    };

    try {
      const { data } = await api.post(ENDPOINTS.updateUserList(), payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (data?.status === "OK") {
        setMoveStatus(true);
        setTimeout(() => setMoveStatus(false), 3000);

        // Remove from current expanded details
        setTalentDetails((prev) => prev.filter((t) => String(t.userId) !== payload.userId));

        // Update counters locally
        setSubmissions((prev) =>
          prev.map((role) => {
            if (role.roleId !== roleId) return role;
            const updated = { ...role };

            const currentKey = listKeyMap[payload.currentList];
            const targetKey = listKeyMap[payload.targetList];

            if (currentKey && updated[currentKey]) {
              updated[currentKey] = updated[currentKey].filter((id) => id !== payload.userId);
            }
            if (targetKey && updated[targetKey] && !updated[targetKey].includes(payload.userId)) {
              updated[targetKey] = [...updated[targetKey], payload.userId];
            }
            return updated;
          })
        );
      } else {
        showError("Failed to move talent.");
      }
    } catch (err) {
      showError("Failed to move talent.");
    }
  };

  // Messaging
  const handleMessageClick = (talent) => {
    const enriched = {
      ...talent,
      talentName: `${talent.firstName || talent.userFirstName || ""} ${talent.lastName || talent.userLastName || ""}`.trim(),
      talentEmail: talent.userEmail,
      noticeId,
      noticeTitle: projectTitle,
      originalMessageId: talent.originalMessageId || "0",
      talentImage: talent.talentImage || talent.profileImage || talent.imageUrl || null,
    };
    setSelectedMessage(enriched);
    setIsReplyDrawerOpen(true);
  };

  // Multi-select toggle
  const toggleSelection = (talent) => {
    setSelectedTalents((prevSelected) => {
      if (prevSelected.some((t) => t.userId === talent.userId)) {
        return prevSelected.filter((t) => t.userId !== talent.userId);
      }
      return [...prevSelected, talent];
    });
  };

  // Share links
  const getShareLinks = () => {
    const url = encodeURIComponent(`https://yourwebsite.com/casting-call/${noticeId}`);
    const text = encodeURIComponent(`Casting Call: ${projectTitle}\n${url}`);
    return {
      WhatsApp: `https://api.whatsapp.com/send?text=${text}`,
      Gmail: `mailto:?subject=${encodeURIComponent("Casting Call: " + projectTitle)}&body=${text}`,
      Twitter: `https://twitter.com/intent/tweet?text=${text}`,
      LinkedIn: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${encodeURIComponent(projectTitle)}`,
      Facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${encodeURIComponent("Casting Call: " + projectTitle)}`,
    };
  };

  const handleShare = (e, notice) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setShareNotice(notice);
    setSharePos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setTimeout(() => setShowShareMenu(true), 0);
  };

  // Close share menu on outside click
  useEffect(() => {
    const onClick = (ev) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(ev.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div>
      <Navbar />

      {/* Full-screen loader for initial load */}
      {isPageLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(255,255,255,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <AppLoader message="Loading project…" />
        </Box>
      )}

      <div style={{ backgroundColor: "#eee", minHeight: "100vh" }}>
        <div
          className="container mx-auto p-4"
          style={{ maxWidth: "1200px", margin: "auto", paddingTop: "30px" }}
        >
          <h1 className="text-2xl font-bold">
            <i>{projectTitle}</i>
          </h1>

          {/* View Notice & Share Buttons 
          <div style={{ margin: "16px 0", display: "flex", justifyContent: "space-between" }}>
            <button
              onClick={() => {
                setSelectedNotice({ noticeId, projectTitle });
                setNoticeDialogOpen(true);
              }}
              className="bg-blue-100 text-blue-800 font-medium py-1 px-3 rounded mr-2"
            >
              View Notice
            </button>

            <IconButton aria-label="Share" onClick={(e) => handleShare(e, { noticeId, projectTitle })} size="small">
              <svg viewBox="0 0 512 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                <path d="M295.204,180.593C132.168,180.593,0,312.759,0,475.796 c51.398-130.047,184.869-203.22,317.483-183.603L295.204,180.593z" />
                <path d="M512,253L295.204,36.204v217.818C159.946,249.655,34.992,339.262,0,475.794 c59.905-109.171,178.724-165.463,295.204-151.033v145.035L512,253z" />
              </svg>
            </IconButton>
          </div>
          */}

          {/* Submissions Table */}
          <SubmissionTable
            submissions={submissions}
            expandedList={expandedList}
            talentDetails={talentDetails}
            fetchTalentDetails={fetchTalentDetails}
            isLoading={isRowLoading}              // row-level loader
            favorites={favorites}
            setFavorites={setFavorites}
            notes={notes}
            setNotes={setNotes}
            editingNote={editingNote}
            setEditingNote={setEditingNote}
            noteText={noteText}
            setNoteText={setNoteText}
            handleFavouriteClick={handleFavouriteClick}
            handleMoveTalent={handleMoveTalent}
            sortBy={sortBy}
            setSortBy={setSortBy}
            handleMessageClick={handleMessageClick}
            navigate={navigate}
            shareMenuRef={shareMenuRef}
            sharePos={sharePos}
            showShareMenu={showShareMenu}
            getShareLinks={getShareLinks}
            handleShare={handleShare}
            moveStatus={moveStatus}
            setMoveStatus={setMoveStatus}
            setTalentDetails={setTalentDetails}
            toggleSelection={toggleSelection}
            selectedTalents={selectedTalents}
            shareNotice={shareNotice}
            noticeId={noticeId}
          />

          {/* Reply Drawer */}
          <ReplyDrawer
            open={isReplyDrawerOpen}
            selectedMessage={selectedMessage}
            onClose={() => setIsReplyDrawerOpen(false)}
          />

          {/* Share Menu Popup */}
          {showShareMenu && shareNotice && sharePos && (
            <SharePopup
              ref={shareMenuRef}
              sharePos={sharePos}
              getShareLinks={() => getShareLinks(shareNotice)}
              onClose={() => setShowShareMenu(false)}
            />
          )}

          {/* Notice Preview Dialog */}
          <NoticePreviewDialog
            open={noticeDialogOpen}
            onClose={() => setNoticeDialogOpen(false)}
            notice={selectedNotice}
          />
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CastingProjectPage;
